import { useState, useEffect } from 'react';
import { fetchApi } from '../../lib/api';
import { useConnections } from '../../hooks/useConnections';

export default function CompanyDirectory() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const { requestToPitch } = useConnections();
  const [requesting, setRequesting] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchApi('/api/companies/directory');
        setCompanies(data.companies || []);
      } catch (err) {
        console.error('Failed to load directory:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleRequest = async (companyId) => {
    setRequesting(companyId);
    try {
      await requestToPitch(companyId);
      // Reload directory to update status
      const data = await fetchApi('/api/companies/directory');
      setCompanies(data.companies || []);
    } catch (err) {
      alert(err.message);
    } finally {
      setRequesting(null);
    }
  };

  if (loading) return <div className="text-sm text-gray-400 p-4">Loading companies...</div>;

  if (companies.length === 0) {
    return (
      <div className="glass p-7">
        <h2 className="font-heading text-xl text-vault-navy mb-4">Company Directory</h2>
        <p className="text-sm text-gray-400">No companies are available yet. Check back later.</p>
      </div>
    );
  }

  const statusBadge = (status) => {
    if (status === 'approved') return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-medium">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Connected
      </span>
    );
    if (status === 'pending') return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-medium">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Pending
      </span>
    );
    if (status === 'declined') return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 border border-red-200 text-red-600 text-[11px] font-medium">
        Declined
      </span>
    );
    return null;
  };

  return (
    <div>
      <h2 className="font-heading text-2xl text-vault-navy mb-5">Company Directory</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {companies.map(company => (
          <div key={company.id} className="glass p-5">
            <div className="flex items-start gap-4">
              {company.logo_url ? (
                <img src={company.logo_url} alt="" className="w-12 h-12 rounded-btn object-cover shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-btn bg-gradient-to-br from-vault-gold to-amber-500 flex items-center justify-center text-white font-bold text-lg shrink-0">
                  {company.company_name?.charAt(0)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-vault-navy text-sm truncate">{company.company_name}</h3>
                  {company.company_type && (
                    <span className="text-[10px] text-gray-400 capitalize shrink-0">{company.company_type}</span>
                  )}
                </div>
                {company.categories?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {company.categories.slice(0, 4).map((cat, i) => (
                      <span key={i} className="px-2 py-0.5 bg-gray-100 rounded-full text-[10px] text-gray-500">{cat}</span>
                    ))}
                    {company.categories.length > 4 && (
                      <span className="px-2 py-0.5 text-[10px] text-gray-400">+{company.categories.length - 4}</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
              {company.connection_status ? (
                statusBadge(company.connection_status)
              ) : (
                <button
                  onClick={() => handleRequest(company.id)}
                  disabled={requesting === company.id}
                  className="px-4 py-2 rounded-btn text-sm font-semibold text-white disabled:opacity-50 transition-all"
                  style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
                >
                  {requesting === company.id ? 'Requesting...' : 'Request to Pitch'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
